<script lang="ts">
export default {
  name: 'AccountDataReadinessWizard',
};
</script>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CircleCheck, Flag, TriangleAlert, X } from '@lucide/vue';
import { Account } from '@/types/Account';
import WizardStepper from '@/components/v2/WizardStepper.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Step1Binders from '@/views/account/data_readiness/Step1Binders.vue';
import Step2PolicyDocs from '@/views/account/data_readiness/Step2PolicyDocs.vue';
import Step3EpicCleanup from '@/views/account/data_readiness/Step3EpicCleanup.vue';
import Step4ClientDemographics from '@/views/account/data_readiness/Step4ClientDemographics.vue';
import Step5PolicyDetails from '@/views/account/data_readiness/Step5PolicyDetails.vue';
import Step6ProgramGrouping from '@/views/account/data_readiness/Step6ProgramGrouping.vue';
import Step7Review from '@/views/account/data_readiness/Step7Review.vue';
import { provideDataReadinessWizard, WIZARD_STEPS } from '@/composables/useDataReadinessWizard';

const props = defineProps<{
  account: Account;
}>();

const route = useRoute();
const router = useRouter();

const accountIdRef = computed(() => props.account.accountID);
const wizard = provideDataReadinessWizard(accountIdRef);

const stepFromQuery = computed(() => {
  const raw = Array.isArray(route.query.step) ? route.query.step[0] : route.query.step;
  const n = parseInt(String(raw ?? '1'), 10);
  if (Number.isNaN(n)) return 1;
  return Math.min(Math.max(n, 1), WIZARD_STEPS.length);
});

function syncFromQuery() {
  wizard.setStep(stepFromQuery.value - 1);
}

// Initialize step from query synchronously so the first render lands on the
// correct step component (e.g. direct nav to ?step=2 renders Step 2, not Step 1).
syncFromQuery();

async function goToStep(n: number) {
  // Let the active step persist its data (auto-save on navigate) before moving.
  await wizard.runNavGuards();
  const clamped = Math.min(Math.max(n, 1), WIZARD_STEPS.length);
  router.replace({ query: { ...route.query, step: String(clamped) } });
}

// Server-loaded state can set currentStep when the URL has no ?step. Without
// this sync, Back/Next would write a URL value that matches stepFromQuery's
// default (1), the watcher wouldn't fire, and the user would be stuck.
watch(
  () => wizard.currentStep.value,
  (n) => {
    const expected = n + 1;
    if (expected !== stepFromQuery.value) {
      router.replace({ query: { ...route.query, step: String(expected) } });
    }
  },
);

const stepComponent = computed(() => {
  switch (wizard.currentStep.value) {
    case 0:
      return Step1Binders;
    case 1:
      return Step2PolicyDocs;
    case 2:
      return Step3EpicCleanup;
    case 3:
      return Step4ClientDemographics;
    case 4:
      return Step5PolicyDetails;
    case 5:
      return Step6ProgramGrouping;
    case 6:
      return Step7Review;
    default:
      return Step1Binders;
  }
});

const canGoBack = computed(() => wizard.currentStep.value > 0);
const canGoNext = computed(() => wizard.currentStep.value < WIZARD_STEPS.length - 1);

// Overall readiness for the header pill: unknown until every prior step's
// issue count has loaded (the review step's own entry is always null).
const readiness = computed<'ready' | 'not-ready' | null>(() => {
  const counts = wizard.stepIssueCounts.value.slice(0, -1);
  if (counts.some((c) => c == null)) return null;
  return counts.every((c) => c === 0) ? 'ready' : 'not-ready';
});

const clientName = computed(() => props.account.displayName || props.account.accountName);
const clientInitials = computed(() => {
  return clientName.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
});

async function exitWizard() {
  // Let the active step persist its data before leaving (auto-save on navigate).
  await wizard.runNavGuards();
  router.push({ name: 'account-client-readiness', params: { id: props.account.accountID } });
}

async function finishWizard() {
  // The review step can veto the exit (confirmation when issues remain).
  if (!(await wizard.runBeforeFinish())) return;
  await exitWizard();
}

watch(stepFromQuery, syncFromQuery);
</script>

<template>
  <!-- w-full is load-bearing: inside AccountView's flex column, mx-auto alone
       would shrink-to-fit the widest step's content, making the card width
       jump between steps. With it, width is viewport-defined: 100% up to 80rem. -->
  <div class="mx-auto flex w-full max-w-7xl flex-col px-2">
    <div class="mt-2 w-full flex-1 rounded-lg border border-solid border-base-300 bg-base-100 shadow-sm">
      <!-- Header row: client identity + readiness pill -->
      <div class="flex items-center gap-3 border-b border-base-300 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
            {{ clientInitials }}
          </div>
          <div class="flex min-w-0 flex-col items-start">
            <div class="truncate text-sm leading-tight font-semibold">{{ clientName }}</div>
            <div class="text-xs text-base-content/60">Data Readiness</div>
          </div>
        </div>

        <div class="flex-1"></div>

        <span
          v-if="readiness === 'ready'"
          class="flex shrink-0 items-center gap-1.5 rounded-full bg-[#E1F5EE] px-3 py-1 text-xs font-semibold text-[#0F6E56]"
          data-testid="wizard-readiness-pill">
          <CircleCheck :size="13" />
          Ready
        </span>
        <span
          v-else-if="readiness === 'not-ready'"
          class="flex shrink-0 items-center gap-1.5 rounded-full bg-[#FAEEDA] px-3 py-1 text-xs font-semibold text-[#854F0B]"
          data-testid="wizard-readiness-pill">
          <TriangleAlert :size="13" />
          Not ready
        </span>

        <button
          class="btn btn-square shrink-0 opacity-60 btn-ghost transition-opacity btn-sm hover:opacity-100"
          aria-label="Close wizard"
          data-testid="exit-data-readiness-wizard"
          @click="exitWizard">
          <X :size="18" />
        </button>
      </div>

      <!-- Stepper track row -->
      <div class="border-b border-base-300 px-6 py-3">
        <WizardStepper
          class="w-full"
          :steps="[...WIZARD_STEPS]"
          :current-step="wizard.currentStep.value"
          :step-issues="wizard.stepIssueCounts.value"
          :completed-through="wizard.maxStepReached.value"
          @step-click="goToStep($event + 1)" />
      </div>

      <!-- Step body -->
      <component :is="stepComponent" />

      <!-- Footer -->
      <div class="flex items-center justify-between gap-3 border-t border-base-300 bg-base-200/40 px-4 py-3">
        <AppButton
          v-if="canGoBack"
          secondary
          @click="goToStep(wizard.currentStep.value)">
          ← Back
        </AppButton>
        <AppButton
          v-else
          secondary
          @click="exitWizard">
          ← Back to Client Readiness
        </AppButton>
        <div class="flex items-center gap-3">
          <span
            v-if="wizard.footerHint.value"
            class="text-xs"
            :class="{
              'text-error': wizard.footerHint.value.color === 'red',
              'text-warning': wizard.footerHint.value.color === 'amber',
              'text-success': wizard.footerHint.value.color === 'green',
              'text-base-content/60': wizard.footerHint.value.color === 'muted',
            }">
            {{ wizard.footerHint.value.text }}
          </span>
          <AppButton
            v-if="canGoNext"
            primary
            :disabled="!wizard.canAdvance.value"
            @click="goToStep(wizard.currentStep.value + 2)">
            Next →
          </AppButton>
          <AppButton
            v-else
            primary
            @click="finishWizard">
            <Flag :size="14" />
            Finish & exit
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>
